/**
 * Create-ASN wizard (CR-4888 Slice C).
 *
 * 3-step flow: Source & Lines → Shipment Details → Review & Submit.
 * ASN number is auto-generated server-side; distance_km is auto-calculated
 * from supplier ↔ customer geocoded sites — both are read-only on the wire.
 */
import { useState, useEffect, useMemo } from 'react';
import { X, Package } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { purchasingAPI } from '../../lib/api';
import { useMenuSettings } from '../../contexts/MenuSettingsContext';
import { ASNModalProps, ASNScheduleDetail } from './asnModalTypes';
import { StepIndicator } from './asn-wizard/StepIndicator';
import { SourceLinesStep } from './asn-wizard/SourceLinesStep';
import { ShipmentDetailsStep } from './asn-wizard/ShipmentDetailsStep';
import { ReviewStep } from './asn-wizard/ReviewStep';
import { ImportASNLinesModal } from './asn-wizard/ImportASNLinesModal';
import { useCallOffShipmentDefaults } from './asn-wizard/useCallOffShipmentDefaults';
import {
  ASNFormData,
  DeliveryLineOption,
  INITIAL_FORM_DATA,
  SourceType,
  StepIndex,
} from './asn-wizard/types';

export function CreateASNModal({
  isOpen, onClose, onSubmit, initialCallOffId, initialDeliveryLineIds, initialDeliveryOption,
}: ASNModalProps) {
  const { isFeatureEnabled } = useMenuSettings();
  const [currentStep, setCurrentStep] = useState<StepIndex>(1);
  const [sourceType, setSourceType] = useState<SourceType>(
    initialCallOffId || (initialDeliveryLineIds && initialDeliveryLineIds.length > 0)
      ? 'delivery_lines'
      : 'schedules'
  );
  const [formData, setFormData] = useState<ASNFormData>(INITIAL_FORM_DATA);
  // CR-6038: which delivery-line source the wizard is showing. 'CallOff' lists
  // call-off-driven lines; 'SupplierImport' lists imported-but-unallocated
  // lines so they aren't stranded after an import session. Seeded to imported
  // when the Import & Allocate "Create new ASN" route pre-loads specific lines.
  const [deliveryLineSource, setDeliveryLineSource] = useState<'CallOff' | 'SupplierImport'>(
    initialDeliveryLineIds && initialDeliveryLineIds.length > 0 ? 'SupplierImport' : 'CallOff'
  );
  const [submitting, setSubmitting] = useState(false);
  const [schedules, setSchedules] = useState<ASNScheduleDetail[]>([]);
  const [deliveryLines, setDeliveryLines] = useState<DeliveryLineOption[]>([]);
  const [loadingSchedules, setLoadingSchedules] = useState(true);
  const [loadingDeliveryLines, setLoadingDeliveryLines] = useState(true);
  const [isImportOpen, setIsImportOpen] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    // CR-6038: a fresh open defaults to imported lines when the Import &
    // Allocate route seeded specific ids, otherwise call-off lines. Pass the
    // resolved source straight to the fetch so it can't race the state set.
    const initialDLSource: 'CallOff' | 'SupplierImport' =
      initialDeliveryLineIds && initialDeliveryLineIds.length > 0 ? 'SupplierImport' : 'CallOff';
    setDeliveryLineSource(initialDLSource);
    if (initialDeliveryLineIds && initialDeliveryLineIds.length > 0) {
      // CR-6038: standalone Import & Allocate workflow pre-loaded specific
      // imported lines to route into this new ASN. Seed the selection here
      // so the wizard opens already pointing at those lines; the available-
      // delivery-lines fetch below will populate their details.
      setSourceType('delivery_lines');
      setFormData((prev) => ({
        ...prev,
        delivery_line_ids: Array.from(new Set([...prev.delivery_line_ids, ...initialDeliveryLineIds])),
      }));
    }
    if (initialCallOffId) {
      setSourceType('delivery_lines');
    }
    fetchAvailableSchedules();
    fetchAvailableDeliveryLines(initialDLSource);
  }, [isOpen, initialCallOffId, initialDeliveryLineIds, initialDeliveryOption]);

  const fetchAvailableSchedules = async () => {
    try {
      setLoadingSchedules(true);
      const response = await purchasingAPI.getSchedules({
        status: 'scheduled',
        ordering: 'schedule_date',
      });
      setSchedules(response.data.results);
    } catch (error) {
      console.error('Failed to fetch schedules:', error);
    } finally {
      setLoadingSchedules(false);
    }
  };

  const fetchAvailableDeliveryLines = async (srcOverride?: 'CallOff' | 'SupplierImport') => {
    try {
      setLoadingDeliveryLines(true);
      // CR-6038: the wizard's delivery-lines source is user-switchable between
      // 'CallOff' (call-off-driven lines) and 'SupplierImport' (imported-but-
      // unallocated lines from either import path). Caller may pass the source
      // explicitly to avoid racing the deliveryLineSource state set.
      const source = srcOverride ?? deliveryLineSource;
      const response = await purchasingAPI.getAvailableDeliveryLines({ source_type: source });
      const lines = response.data as unknown as DeliveryLineOption[];
      setDeliveryLines(lines);
      if (initialCallOffId) {
        // CR-7367: when the call-off button named a destination, tick only that
        // side. A mixed load raises one ASN per destination, and dragging the
        // other side onto it is the mistake the two separate buttons prevent.
        const matchIds = lines
          .filter((l) => l.call_off === initialCallOffId)
          .filter((l) => !initialDeliveryOption || l.delivery_option === initialDeliveryOption)
          .map((l) => l.id);
        if (matchIds.length > 0) {
          setFormData((prev) => ({
            ...prev,
            delivery_line_ids: Array.from(new Set([...prev.delivery_line_ids, ...matchIds])),
          }));
        }
      }
    } catch (error) {
      console.error('Failed to fetch delivery lines:', error);
    } finally {
      setLoadingDeliveryLines(false);
    }
  };

  const handleFieldChange = (field: keyof ASNFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSourceChange = (newSource: SourceType) => {
    setSourceType(newSource);
    setFormData((prev) => ({ ...prev, schedules: [], delivery_line_ids: [] }));
  };

  // CR-6038: switch the delivery-lines tab between call-off and imported
  // sources. Clears the current selection (the two sources list different
  // lines) and refetches with the chosen source.
  const handleDeliveryLineSourceChange = (src: 'CallOff' | 'SupplierImport') => {
    setSourceType('delivery_lines');
    setDeliveryLineSource(src);
    setFormData((prev) => ({ ...prev, schedules: [], delivery_line_ids: [] }));
    fetchAvailableDeliveryLines(src);
  };

  const handleScheduleToggle = (id: number) => {
    setFormData((prev) => {
      const next = prev.schedules.includes(id)
        ? prev.schedules.filter((s) => s !== id)
        : [...prev.schedules, id];
      return { ...prev, schedules: next };
    });
  };

  const handleDeliveryLineToggle = (id: number) => {
    setFormData((prev) => {
      const next = prev.delivery_line_ids.includes(id)
        ? prev.delivery_line_ids.filter((l) => l !== id)
        : [...prev.delivery_line_ids, id];
      return { ...prev, delivery_line_ids: next };
    });
  };

  // The one call-off the selected delivery lines belong to — null for PO
  // schedules, imported lines, or lines spanning call-offs. Opened from a
  // call-off, that call-off counts before its lines have loaded and been ticked.
  // Applies however the modal was opened: picking a call-off's lines on the ASN
  // page works the same as Create ASN from the call-off.
  const selectedCallOffId = useMemo((): number | null => {
    if (sourceType !== 'delivery_lines') return null;
    if (formData.delivery_line_ids.length === 0) return initialCallOffId ?? null;
    const callOffIds = new Set(
      deliveryLines.filter((l) => formData.delivery_line_ids.includes(l.id)).map((l) => l.call_off),
    );
    if (callOffIds.size !== 1) return null;
    const [callOffId] = Array.from(callOffIds);
    return callOffId ?? null;
  }, [sourceType, formData.delivery_line_ids, deliveryLines, initialCallOffId]);

  // Pickup date + tracking number from that call-off (blanks only, so a
  // supplier's manual edits stick), and CR-8190: the haulier the customer
  // appointed on it.
  const {
    haulierName, loading: carrierLoading, failed: carrierLookupFailed,
  } = useCallOffShipmentDefaults(selectedCallOffId, setFormData);

  // CR-8190: a load with a haulier is carried by that haulier. The supplier's
  // own courier choice stays in formData and returns if the lock clears.
  const shipment: ASNFormData = haulierName
    ? { ...formData, courier: 'other', courier_other: haulierName }
    : formData;

  const handleSelectAllInGroup = (lines: DeliveryLineOption[]) => {
    const lineIds = lines.map((l) => l.id);
    setFormData((prev) => {
      const allSelected = lineIds.every((id) => prev.delivery_line_ids.includes(id));
      const next = allSelected
        ? prev.delivery_line_ids.filter((id) => !lineIds.includes(id))
        : Array.from(new Set([...prev.delivery_line_ids, ...lineIds]));
      return { ...prev, delivery_line_ids: next };
    });
  };

  const handleExcelImported = async (importedIds: number[]) => {
    // The in-wizard "Import from Excel" creates SupplierImport lines just like
    // the standalone flow, so switch to the imported source and refetch with
    // it — otherwise the just-imported lines stay hidden behind the call-off
    // filter (same stranding the Imported Lines tab fixes).
    setSourceType('delivery_lines');
    setDeliveryLineSource('SupplierImport');
    setFormData((prev) => ({
      ...prev,
      schedules: [],
      delivery_line_ids: Array.from(new Set([...prev.delivery_line_ids, ...importedIds])),
    }));
    await fetchAvailableDeliveryLines('SupplierImport');
    setIsImportOpen(false);
  };

  // CR-6038: Weight (kg) is user-entered only when the wizard is on the
  // supplier-imported delivery-lines source; call-off lines and PO-line
  // schedules keep the auto-calculated weight.
  const isSupplierImport = sourceType === 'delivery_lines' && deliveryLineSource === 'SupplierImport';

  const hasSelection =
    sourceType === 'schedules'
      ? formData.schedules.length > 0
      : formData.delivery_line_ids.length > 0;

  // CR-6160: tracking number is only mandatory when the paid ASN logistics-
  // tracking module is on for the customer; otherwise it's optional (AC-C2).
  const trackingRequired = isFeatureEnabled('asn_logistics_tracking');
  const shipmentDetailsValid =
    !carrierLoading &&
    (!trackingRequired || !!shipment.tracking_number) &&
    !!shipment.expected_delivery_date &&
    (shipment.courier !== 'other' || !!shipment.courier_other);

  const canAdvance =
    currentStep === 1 ? hasSelection : currentStep === 2 ? shipmentDetailsValid : true;

  const resetAndClose = () => {
    setFormData(INITIAL_FORM_DATA);
    setSourceType('schedules');
    setDeliveryLineSource('CallOff');
    setCurrentStep(1);
    onClose();
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const payload: Record<string, string | number | boolean | number[]> = {
        tracking_number: shipment.tracking_number,
        invoice_number: shipment.invoice_number,
        method_of_transport: shipment.method_of_transport,
        expected_delivery_date: shipment.expected_delivery_date,
        notes: shipment.notes,
      };
      // CR-8190: a load with a haulier sends no carrier — the server names the
      // call-off's haulier as it stands at submit, so a customer reassigning
      // it while this wizard is open cannot get the notice refused.
      if (!haulierName) {
        payload['courier'] = shipment.courier;
        payload['courier_other'] = shipment.courier_other;
      }
      // CR-6041: every ASN may carry a typed weight; the server decides
      // whether it or the calculated total is saved (services.asn_weight).
      if (formData.weight_kg.trim() !== '') {
        payload['weight_kg'] = formData.weight_kg.trim();
      }
      if (sourceType === 'schedules') {
        payload['schedules'] = formData.schedules;
      } else {
        payload['delivery_line_ids'] = formData.delivery_line_ids;
      }
      await onSubmit(payload as Record<string, string | number | boolean>);
      resetAndClose();
    } catch (error) {
      console.error('Failed to create ASN:', error);
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <Card className="w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b sticky top-0 bg-white z-10">
          <div className="flex items-center gap-2">
            <Package className="h-5 w-5 text-blue-600" />
            <h3 className="text-lg font-semibold">Create Advanced Shipping Notice</h3>
          </div>
          <button
            onClick={resetAndClose}
            className="p-1 hover:bg-muted rounded-full transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <CardContent className="p-4 space-y-4">
          <StepIndicator currentStep={currentStep} />

          {currentStep === 1 && (
            <SourceLinesStep
              sourceType={sourceType}
              onSourceChange={handleSourceChange}
              formData={formData}
              onToggleSchedule={handleScheduleToggle}
              onToggleDeliveryLine={handleDeliveryLineToggle}
              onSelectAllInGroup={handleSelectAllInGroup}
              onOpenExcelImport={() => setIsImportOpen(true)}
              schedules={schedules}
              deliveryLines={deliveryLines}
              loadingSchedules={loadingSchedules}
              loadingDeliveryLines={loadingDeliveryLines}
              deliveryLineSource={deliveryLineSource}
              onDeliveryLineSourceChange={handleDeliveryLineSourceChange}
            />
          )}
          {currentStep === 2 && (
            <ShipmentDetailsStep
              formData={formData}
              onChange={handleFieldChange}
              isCallOffSourced={sourceType === 'delivery_lines' && !isSupplierImport}
              isSupplierImport={isSupplierImport}
              haulierName={haulierName}
              carrierLoading={carrierLoading}
              carrierLookupFailed={carrierLookupFailed}
            />
          )}
          {currentStep === 3 && (
            <ReviewStep sourceType={sourceType} formData={shipment} isSupplierImport={isSupplierImport} />
          )}

          <div className="flex justify-between gap-3 pt-2 border-t">
            <button
              type="button"
              onClick={resetAndClose}
              disabled={submitting}
              className="px-4 py-2 text-sm font-medium border rounded-lg hover:bg-muted transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <div className="flex gap-3">
              {currentStep > 1 && (
                <button
                  type="button"
                  onClick={() => setCurrentStep((s) => (s - 1) as StepIndex)}
                  disabled={submitting}
                  className="px-4 py-2 text-sm font-medium border rounded-lg hover:bg-muted transition-colors disabled:opacity-50"
                >
                  Back
                </button>
              )}
              {currentStep < 3 ? (
                <button
                  type="button"
                  onClick={() => setCurrentStep((s) => (s + 1) as StepIndex)}
                  disabled={!canAdvance}
                  className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  Next
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={submitting || !hasSelection || !shipmentDetailsValid}
                  className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  <Package className="h-4 w-4" />
                  {submitting ? 'Creating...' : 'Create ASN'}
                </button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <ImportASNLinesModal
        isOpen={isImportOpen}
        onClose={() => setIsImportOpen(false)}
        onImported={handleExcelImported}
      />
    </div>
  );
}
